/**
 * The plan header's ⋯ menu — the actions a planner uses less than once per visit
 * ("Say it once", docs/design-system.md): Export, Share, Save as Account Rate Card, and
 * Delete. The header itself keeps only the status move (Propose, Mark Won …).
 *
 * Export keeps its own options panel (variant + version + format), so choosing "Export…"
 * closes the menu and opens that panel anchored under the ⋯ button.
 */

import { useRouter } from "@tanstack/react-router";
import { BookmarkPlus, Download, MoreHorizontal, Share2, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { PlanExportPanel } from "@/components/plans/PlanExportMenu";
import {
  SaveAccountRateCardDialog,
  useSaveAccountRateCardOffer,
} from "@/components/plans/SaveAccountRateCard";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useDeleteResourcePlan } from "@/hooks/use-resource-plans";
import type { ResourcePlanDetailView } from "@/schemas/resource-plans";

async function copyOverviewLink(planId: string): Promise<void> {
  const url = `${window.location.origin}/dashboard/plans/${planId}/overview`;
  try {
    await navigator.clipboard.writeText(url);
    toast.success("Overview link copied");
  } catch {
    /*
     * A clipboard write is refusable — a denied `clipboard-write` permission, a non-secure
     * context, or a browser that wants a closer user gesture than this handler counts as.
     * Failing with only "could not copy" would leave the sharer with no way to reach the
     * link at all, so the URL itself goes on screen to be selected by hand.
     */
    toast.error("Could not copy the overview link — copy it from here", {
      description: url,
      duration: 30_000,
    });
  }
}

export function PlanMoreMenu({ plan }: { plan: ResourcePlanDetailView }) {
  const router = useRouter();
  const remove = useDeleteResourcePlan();
  const rateCardOffer = useSaveAccountRateCardOffer(plan.id);
  const [exportOpen, setExportOpen] = useState(false);
  const [rateCardOpen, setRateCardOpen] = useState(false);
  const canDelete = plan.status === "Draft" && plan.canEdit;

  return (
    <div className="relative">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="sm" variant="outline" aria-label="More plan actions">
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64">
          <DropdownMenuItem onSelect={() => setExportOpen(true)}>
            <Download />
            Export…
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => void copyOverviewLink(plan.id)}>
            <Share2 />
            Copy share link
          </DropdownMenuItem>
          {rateCardOffer.offered && (
            <DropdownMenuItem onSelect={() => setRateCardOpen(true)} title={rateCardOffer.title}>
              <BookmarkPlus />
              Save as Account Rate Card…
            </DropdownMenuItem>
          )}
          {canDelete && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-destructive focus:text-destructive [&_svg]:text-destructive"
                disabled={remove.isPending}
                onSelect={() => {
                  if (!window.confirm(`Delete "${plan.name}"? Only an Open plan can be deleted.`))
                    return;
                  remove.mutate(plan.id, {
                    onSuccess: () => router.navigate({ to: "/dashboard/plans" }),
                  });
                }}
              >
                <Trash2 />
                Delete plan
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      <PlanExportPanel plan={plan} open={exportOpen} onOpenChange={setExportOpen} />
      <SaveAccountRateCardDialog
        open={rateCardOpen}
        onOpenChange={setRateCardOpen}
        planId={plan.id}
      />
    </div>
  );
}
